import { useEffect, useState } from 'react';
import { CheckCircle, CloudArrowUp, WarningCircle } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import FileUploader from '../../ui/FileUploader';
import Modal from '../../ui/Modal';
import { Mono } from '../../ui/Mono';
import Textarea from '../../ui/Textarea';
import { FormatLabel } from '../production/productionLabels';
import { CloseBlocksNotice, MissingItemsList, ShipmentProgressMeter } from './ShipmentChecklistPanel';
import { IsCloseBlockedError, ParseCloseBlockedError, ParseClosedSummary } from './shipmentModel';
import { CloseShipment } from './shippingApi';

/**
 * FECHAR O EMBARQUE — e o PONTO DE ANEXO DA MÍDIA.
 *
 * ── A mídia NÃO tem uploader novo ───────────────────────────────────────────
 * §7: "módulo pronto, apenas ponto de integração". Aqui usamos o `FileUploader`
 * que a tela de romaneio já usa, e os arquivos viajam no campo `files` do
 * multipart do fechamento; quem grava é o BACKEND, chamando
 * `WmsService.uploadOutboundFiles` (multer → S3 → `wms_outbound_photos`,
 * rótulo `collection`). A prova fica no ROMANEIO, que é onde ela mora e onde a
 * tela do romaneio já sabe mostrá-la.
 *
 * ── O 409 QUE NÃO É FALHA ───────────────────────────────────────────────────
 * A trava de 100% é refeita no servidor DENTRO da transação, com os dados do
 * banco. Entre esta tela mostrar "9 de 9" e o clique no botão cabe um item novo
 * no checklist (o fracionado do M7 continua criando volume enquanto o caminhão
 * carrega). Quando isso acontece o servidor responde
 * `WMS26_SHIPMENT_CLOSE_BLOCKED` — e tratar isso como "erro ao fechar" seria
 * mentir duas vezes: o Supervisor não errou, e o problema tem nome e lista.
 *
 * Por isso o 409 troca o modal para um estado PRÓPRIO que:
 *   - diz o que aconteceu ("o checklist mudou desde que esta tela carregou");
 *   - mostra os motivos do SERVIDOR e o que falta, item por item, com o
 *     progresso FRESCO que veio no próprio 409;
 *   - avisa que a mídia já subiu (ela sobe ANTES da transação — reenviar
 *     duplicaria a prova no romaneio);
 *   - oferece a única ação que faz sentido: atualizar o checklist e ir bipar.
 */

const PHASE_FORM = 'FORM';
const PHASE_BLOCKED = 'BLOCKED';
const PHASE_DONE = 'DONE';

const MAX_MEDIA_FILES = 10;

const ShipmentCloseModal = ({ isOpen, onClose, shipment, label, onClosed, onNeedsRefresh }) => {
    const [files, setFiles] = useState([]);
    const [notes, setNotes] = useState('');
    const [phase, setPhase] = useState(PHASE_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [blocked, setBlocked] = useState(null);
    const [mediaAlreadySent, setMediaAlreadySent] = useState(0);
    const [result, setResult] = useState(null);

    // Reabrir o modal recomeça do zero — menos o que o servidor já recebeu.
    useEffect(() => {
        if (isOpen) {
            setPhase(PHASE_FORM);
            setFiles([]);
            setNotes('');
            setErrorMessage(null);
            setBlocked(null);
            setMediaAlreadySent(0);
            setResult(null);
        }
    }, [isOpen, shipment?.id]);

    const handleClose = async () => {
        if (!shipment?.id) {
            return;
        }
        setSubmitting(true);
        setErrorMessage(null);
        try {
            const body = new FormData();
            for (const file of files) {
                body.append('files', file);
            }
            if (notes.trim()) {
                body.append('notes', notes.trim());
            }
            const payload = await CloseShipment(shipment.id, body);
            setResult(payload);
            setPhase(PHASE_DONE);
            onClosed?.(payload);
        } catch (error) {
            if (IsCloseBlockedError(error)) {
                // A mídia sobe ANTES da transação: se havia arquivo, ele já foi
                // anexado ao romaneio mesmo com o fechamento recusado. Guardar a
                // contagem e ESVAZIAR a seleção evita a duplicata que aconteceria
                // se o Supervisor clicasse de novo com os mesmos arquivos.
                setMediaAlreadySent(files.length);
                setFiles([]);
                setBlocked(ParseCloseBlockedError(error));
                setPhase(PHASE_BLOCKED);
                return;
            }
            setErrorMessage(error?.message || label('wms.common.load_error'));
        } finally {
            setSubmitting(false);
        }
    };

    const summary = result ? ParseClosedSummary(result) : null;
    // O 409 tem duas caras: "entrou item novo / checklist vazio" (o Supervisor
    // volta e bipa) e "este embarque não está mais aberto" (alguém já fechou).
    // A regra do backend é a mesma; a instrução para a pessoa não é.
    const checklistChanged = (blocked?.rules || []).some(
        (rule) => rule === 'NOT_FULLY_SCANNED' || rule === 'EMPTY_CHECKLIST',
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={submitting ? () => {} : onClose}
            title={label('wms.shipment.close_title')}
            icon={<CloudArrowUp size={22} />}
            size="lg"
            actions={
                phase === PHASE_FORM ? (
                    <>
                        <Button variant="secondary" onClick={onClose} disabled={submitting}>
                            {label('wms.common.cancel')}
                        </Button>
                        <Button onClick={handleClose} loading={submitting} disabled={shipment?.can_close !== true}>
                            {label('wms.shipment.close_confirm')}
                        </Button>
                    </>
                ) : phase === PHASE_BLOCKED ? (
                    <>
                        <Button variant="secondary" onClick={onClose}>
                            {label('wms.common.close')}
                        </Button>
                        <Button
                            onClick={() => {
                                onNeedsRefresh?.();
                                onClose?.();
                            }}
                        >
                            {label('wms.shipment.close_reload')}
                        </Button>
                    </>
                ) : (
                    <Button onClick={onClose}>{label('wms.common.close')}</Button>
                )
            }
        >
            {phase === PHASE_FORM ? (
                <div className="space-y-5">
                    {/* O estado da trava vem primeiro: quem abre este modal
                        precisa ver o número antes de escolher arquivo. */}
                    <ShipmentProgressMeter shipment={shipment} label={label} />
                    <MissingItemsList shipment={shipment} label={label} />
                    {shipment?.can_close === false ? <CloseBlocksNotice source={shipment} label={label} /> : null}

                    <section className="space-y-2">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-mono-600 dark:text-mono-300">
                            {label('wms.shipment.close_media')}
                        </h4>
                        <p className="text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.shipment.close_media_hint')}
                        </p>
                        <FileUploader
                            files={files}
                            onFilesChange={(next) => setFiles(Array.isArray(next) ? next.slice(0, MAX_MEDIA_FILES) : [])}
                            multiple
                            maxFiles={MAX_MEDIA_FILES}
                            accept="image/*,video/*,application/pdf"
                            subtitle={`${files.length}/${MAX_MEDIA_FILES}`}
                            disabled={submitting}
                        />
                    </section>

                    <Textarea
                        label={label('wms.shipment.field.notes')}
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        rows={2}
                        disabled={submitting}
                    />

                    <section className="space-y-1 rounded-xl bg-mono-50 px-4 py-3 dark:bg-mono-900">
                        <p className="text-xs font-bold uppercase tracking-wide text-mono-500">
                            {label('wms.shipment.close_effects')}
                        </p>
                        <ul className="list-disc space-y-0.5 pl-5 text-sm text-mono-600 dark:text-mono-400">
                            <li>{label('wms.shipment.close_effect_writeoff')}</li>
                            <li>{label('wms.shipment.close_effect_lpn')}</li>
                            <li>{label('wms.shipment.close_effect_deposit')}</li>
                            <li>{label('wms.shipment.close_effect_bling')}</li>
                        </ul>
                    </section>

                    {errorMessage ? (
                        <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-text dark:bg-danger-900/30 dark:text-danger-300">
                            {errorMessage}
                        </p>
                    ) : null}
                </div>
            ) : null}

            {phase === PHASE_BLOCKED ? (
                <div className="space-y-4">
                    <div className="flex items-start gap-3 rounded-xl border-2 border-warning-400 bg-warning-soft px-4 py-3 dark:border-warning-700 dark:bg-warning-900/30">
                        <WarningCircle size={22} className="mt-0.5 shrink-0 text-warning-text dark:text-warning-300" />
                        <div className="space-y-1">
                            <p className="text-base font-bold text-warning-text dark:text-warning-200">
                                {label(
                                    checklistChanged
                                        ? 'wms.shipment.close_changed_title'
                                        : 'wms.shipment.close_not_open_title',
                                )}
                            </p>
                            <p className="text-sm text-warning-text dark:text-warning-200">
                                {label(
                                    checklistChanged
                                        ? 'wms.shipment.close_changed_body'
                                        : 'wms.shipment.close_not_open_body',
                                )}
                            </p>
                        </div>
                    </div>

                    {/* O progresso e a lista do 409 — o retrato do SERVIDOR no
                        instante do clique, não o que esta tela tinha em memória.
                        Só aparecem quando o bloqueio É de checklist: num embarque
                        que outra pessoa já fechou, mostrar "falta bipar" seria
                        mandar procurar carga que não existe mais. */}
                    {checklistChanged ? (
                        <>
                            <ShipmentProgressMeter shipment={{ progress: blocked?.progress }} label={label} />
                            <MissingItemsList shipment={{ progress: blocked?.progress }} label={label} />
                        </>
                    ) : null}
                    <CloseBlocksNotice source={{ close_blocks: blocked?.blocks }} label={label} />

                    {mediaAlreadySent > 0 ? (
                        <p className="flex items-start gap-2 rounded-lg bg-info-soft px-3 py-2 text-sm text-info-text dark:bg-info-900/30 dark:text-info-200">
                            <CheckCircle size={16} className="mt-0.5 shrink-0" />
                            {FormatLabel(label('wms.shipment.close_media_kept'), { count: mediaAlreadySent })}
                        </p>
                    ) : null}
                </div>
            ) : null}

            {phase === PHASE_DONE ? (
                <div className="space-y-4">
                    <p className="flex items-center gap-2 text-lg font-bold text-success-text dark:text-success-200">
                        <CheckCircle size={22} className="shrink-0" />
                        {label('wms.shipment.closed_title')}
                    </p>
                    {summary ? (
                        <ul className="space-y-1.5 text-sm text-mono-700 dark:text-mono-300">
                            <li>
                                <Mono className="font-bold">{summary.lpns_closed}</Mono>{' '}
                                {label('wms.shipment.closed_lpns')}
                            </li>
                            <li>
                                <Mono className="font-bold">{summary.volumes_closed}</Mono>{' '}
                                {label('wms.shipment.closed_volumes')}
                            </li>
                            <li>
                                <Mono className="font-bold">{summary.writeoff_movements}</Mono>{' '}
                                {label('wms.shipment.closed_writeoffs')}
                            </li>
                            <li>
                                <Mono className="font-bold">{summary.bling_sync_enqueued}</Mono>{' '}
                                {label('wms.shipment.closed_bling')}
                            </li>
                            {summary.media_attached > 0 ? (
                                <li>
                                    <Mono className="font-bold">{summary.media_attached}</Mono>{' '}
                                    {label('wms.shipment.closed_media')}
                                </li>
                            ) : null}
                            <li className="flex items-center gap-2 pt-1">
                                <span className="text-xs uppercase tracking-wide text-mono-500">
                                    {label('wms.shipment.closed_deposit_rule')}
                                </span>
                                {summary.deposit_rule?.applied ? (
                                    <Badge variant="success">{summary.deposit_rule.deposit_code || '—'}</Badge>
                                ) : (
                                    <span className="text-xs text-mono-500 dark:text-mono-400">
                                        {label('wms.shipment.closed_deposit_none')}
                                    </span>
                                )}
                            </li>
                        </ul>
                    ) : null}
                </div>
            ) : null}
        </Modal>
    );
};

export default ShipmentCloseModal;
